import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview
} from "../../../../components/inputs/FractionInput";
import {
  formatFractionLatex,
  formatMixedFractionLatex,
} from "../../../../utils/fractionLatex";

// ==== POMOCNICZE DO UŁAMKÓW ====

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}

function makeRational(num, den = 1) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function ratToNumber(r) {
  return r.num / r.den;
}

// zwykły ułamek z minusem na zewnątrz (np. -3/2 → "-\\dfrac{3}{2}")
const ratToLatex = (r) => formatFractionLatex(r.num, r.den);

// ułamek mieszany / zwykły, z minusem na zewnątrz
// np. -7/3 → "-2\\dfrac{1}{3}", 5/2 → "2\\dfrac{1}{2}", -1/4 → "-\\dfrac{1}{4}"
const ratToMixedLatex = (r) => formatMixedFractionLatex(r.num, r.den);

// ==== GENERATOR ====

export function generateTask(options = {}) {
  if (options.simple) {
    const a1 = Math.floor(Math.random() * 13) - 6;
    const rOptions = [-4, -3, -2, -1, 1, 2, 3, 4];
    const rValue = rOptions[Math.floor(Math.random() * rOptions.length)];
    const n = Math.floor(Math.random() * 5) + 3;
    const anValue = a1 + (n - 1) * rValue;
    return {
      type: "FindNthTermTask",
      data: {
        a1,
        n,
        rNum: rValue,
        rDen: 1,
        rValue,
        anNum: anValue,
        anDen: 1,
        anValue,
      },
    };
  }

  while (true) {
    // ---- a1: od -9 do 25, bez 0 i 1 ----
    const a1Options = [];
    for (let i = -9; i <= 25; i += 1) {
      if (i !== 0 && i !== 1) a1Options.push(i);
    }
    const a1 =
      a1Options[Math.floor(Math.random() * a1Options.length)];

    // ---- r: 2/3 ułamkowe, 1/3 całkowite, |r| w [2,10], r != 0 ----
    let rRat;

    if (Math.random() < 1 / 3) {
      // 1/3 przypadków: r całkowite
      const intOptions = [];
      for (let k = -10; k <= 10; k += 1) {
        if (k !== 0 && Math.abs(k) >= 2) intOptions.push(k);
      }
      const k =
        intOptions[Math.floor(Math.random() * intOptions.length)];
      rRat = makeRational(k, 1);
    } else {
      // 2/3 przypadków: r ułamkowe o mianownikach 2,3,4
      const denOptions = [2, 3, 4];
      while (true) {
        const den =
          denOptions[Math.floor(Math.random() * denOptions.length)];
        let numAbs =
          Math.floor(Math.random() * 80) + 1; // 1..80

        // chcemy, żeby numAbs/den w przybliżeniu był w [2,10]
        if (numAbs / den < 2 || numAbs / den > 10) continue;

        if (Math.random() < 0.5) numAbs = -numAbs;

        const cand = makeRational(numAbs, den);
        const val = Math.abs(ratToNumber(cand));
        if (val >= 2 && val <= 10) {
          rRat = cand;
          break;
        }
      }
    }

    const rValue = ratToNumber(rRat);

    // ---- n: numer wyrazu 3..12 ----
    const n = Math.floor(Math.random() * 10) + 3;

    // ---- liczymy a_n jako ułamek ----
    // a_n = a1 + (n-1)*r
    const anNum = a1 * rRat.den + (n - 1) * rRat.num;
    const anRat = makeRational(anNum, rRat.den);
    const anValue = ratToNumber(anRat);

    // ograniczamy wielkość a_n, żeby było sensowne
    if (Math.abs(anValue) > 300) continue;

    return {
      type: "FindNthTermTask",
      data: {
        a1,
        n,
        rNum: rRat.num,
        rDen: rRat.den,
        rValue,
        anNum: anRat.num,
        anDen: anRat.den,
        anValue
      }
    };
  }
}

// ==== TREŚĆ ====

export function renderTask({ data }) {
  const { a1, rNum, rDen, n } = data;
  const rMixedLatex = ratToMixedLatex({ num: rNum, den: rDen });

  return (
    <>
      <p>
        Dany jest ciąg arytmetyczny, w którym pierwszy wyraz{" "}
        <InlineMath math={`a_1 = ${a1}`} />, a różnica ciągu{" "}
        {/* całe "r = ..." nierozrywalne */}
        <span style={{ whiteSpace: "nowrap" }}>
          <InlineMath math={`r\\,=\\,${rMixedLatex}.`} />
        </span>
      </p>
      <p>
        Oblicz <InlineMath math={`a_{${n}}`} />.
      </p>
    </>
  );
}

// ==== INPUT ====
// Pokazujemy a_{n} = ... + podgląd z etykietą a_{n}

export function renderInput(task, userAnswer, onChange, disabled) {
  const n = task?.data?.n;

  return (
    <div className="flex items-center gap-2">
      <InlineMath math={`a_{${n}} =`} />
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={`a_{${n}}`}
      />
    </div>
  );
}

// ==== SPRAWDZANIE ====

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const given = userAnswer.parsed;
  if (given == null || Number.isNaN(given)) return false;

  // zapamiętujemy n, żeby renderUserAnswer miał do niego dostęp
  userAnswer.n = data.n;

  return Math.abs(given - data.anValue) < 0.01;
}

// ==== POPRAWNA ODPOWIEDŹ ====

export function renderAnswer({ data }) {
  const { n, anNum, anDen } = data;
  const anMixedLatex = ratToMixedLatex({ num: anNum, den: anDen });

  return <InlineMath math={`a_{${n}} = ${anMixedLatex}`} />;
}

// ==== WYJAŚNIENIE ====

export function renderExplanation({ data }) {
  const { a1, rNum, rDen, n, anNum, anDen } = data;

  const rRat = makeRational(rNum, rDen);
  const rLatex = ratToLatex(rRat); // czysty ułamek (np. -7/3)
  const rMixedLatex = ratToMixedLatex(rRat); // mieszany do komentarza

  const multiplier = n - 1;
  const productRat = makeRational(multiplier * rRat.num, rRat.den);
  const productLatex = ratToLatex(productRat);

  const anRat = makeRational(anNum, anDen);
  const anLatex = ratToLatex(anRat);
  const anMixedLatex = ratToMixedLatex(anRat);

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Wzór na <InlineMath math={"n"} />-ty wyraz
        ciągu arytmetycznego:
      </p>
      <BlockMath math={"a_n = a_1 + (n-1) \\cdot r"} />

      <p>
        <strong>Krok 2:</strong> Podstawiamy dane z treści zadania:
      </p>
      <BlockMath
        math={`a_{${n}} = ${a1} + (${n}-1) \\cdot ${rLatex}`}
      />

      <p>
        <strong>Krok 3:</strong> Obliczamy iloczyn{" "}
        <InlineMath math={"(n-1) \\cdot r"} />:
      </p>
      <BlockMath
        math={`a_{${n}} = ${a1} + ${multiplier} \\cdot ${rLatex}`}
      />
      <BlockMath
        math={`a_{${n}} = ${a1} + ${productLatex}`}
      />

      <p>
        <strong>Krok 4:</strong> Dodajemy do siebie liczby:
      </p>
      <BlockMath math={`a_{${n}} = ${anLatex}`} />
      <p>
        Możemy zapisać wynik w postaci liczby mieszanej:
      </p>
      <BlockMath math={`a_{${n}} = ${anMixedLatex}`} />

      <p>
        <strong>Odpowiedź:</strong>{" "}
        <InlineMath math={`a_{${n}} = ${anMixedLatex}`} />
      </p>
      <p style={{ fontSize: "0.9rem", opacity: 0.8 }}>
        (Różnica ciągu z treści:{" "}
        <span style={{ whiteSpace: "nowrap" }}>
          <InlineMath math={`r\\,=\\,${rMixedLatex}.`} />
        </span>
        )
      </p>
    </div>
  );
}

// ==== ODPOWIEDŹ UŻYTKOWNIKA ====
// a_{n} = (dokładnie to, co wpisał user: ułamek, liczba mieszana itd.)

export function renderUserAnswer(userAnswer, task) {
  const n = userAnswer?.n ?? task?.data?.n ?? "?";

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  // budujemy pełen LaTeX jak w innych zadaniach z FractionInput
  const latex = formatLatexPreview(userAnswer.raw, `a_{${n}}`);

  return <InlineMath math={latex} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer
};
